import React from "react";
import moment from "moment";
import { Table, Tr, Td, Sort } from "reactable";
import { Checkbox, RaisedButton, Popover, Menu, MenuItem } from "material-ui";

import TicketsAtAGlance from "./tickets-at-a-glance.jsx";
import StatsBlockContainer from "./stats-block-container.jsx";
import StatsBlock from "./stats-block.jsx";
import TableRefreshIndicator from "./table-refresh-indicator.jsx";
import InlineLinkIconButton from "./inline-link-icon-button.jsx";
import DownloadTicketsPdfButton from "./download-tickets-pdf-button.jsx";
import GodTicketsExtension from "./overlays/god-tickets-extension.jsx";
import TransferOverlay from "./overlays/transfer-overlay.jsx";
import ExchangeOverlay from "./overlays/exchange-overlay.jsx";
import BulkDeleteOverlay from "./overlays/bulk-delete-overlay.jsx";
import ChangeSeatsOverlay from "./overlays/change-seats-overlay.jsx";
import RefundOverlay from "./overlays/refund.jsx";
import LoadingOverlay from "./overlays/loading.jsx";

import UserStore from "../../stores/UserStore";
import TicketActions from "../../actions/TicketActions";
import OrderActions from "../../actions/OrderActions";
import LedgerActions from "../../actions/LedgerActions.js";
import GlobalNotificationActions from "../../actions/GlobalNotificationActions";
import OrderUtil from "../../utils/OrderUtil";

const PAGE_SIZE = 20;

const DELETED_ROW_STYLE = { textDecoration: "line-through", color: "#ccc" };
const HIGHLIGHTED_ROW_STYLE = {
  backgroundColor: "#fff176",
  boxShadow: "1px 1px 10px rgba(0,0,0,0.2)"
};

class TicketTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      deletedTickets: {},
      selectedTickets: {},
      highlightedRow: null,
      currentPage: 0,
      showExtensionsModal: false,
      currentlyTransferring: null,
      refundOptions: null,
      ticketsToExchange: null,
      ticketsToDelete: null,
      ticketsToRefund: null,
      ticketsToChangeSeats: null
    };

    this._makeRowForTicket = this._makeRowForTicket.bind(this);
    this._refresh = this._refresh.bind(this);
    this._onCheckAll = this._onCheckAll.bind(this);
    this._onCheck = this._onCheck.bind(this);
    this._onExtensionFormClose = this._onExtensionFormClose.bind(this);
    this._onExtensionSubmit = this._onExtensionSubmit.bind(this);

    this._closeTransferModal = this._closeTransferModal.bind(this);
    this._submitTransfer = this._submitTransfer.bind(this);
    this._submitTransfer = this._submitTransfer.bind(this);

    this._showExchangeModal = this._showExchangeModal.bind(this);
    this._closeExchangeModal = this._closeExchangeModal.bind(this);
    this._onExchangeTickets = this._onExchangeTickets.bind(this);

    this._showDeleteModal = this._showDeleteModal.bind(this);
    this._closeDeleteModal = this._closeDeleteModal.bind(this);
    this._onDeleteTickets = this._onDeleteTickets.bind(this);

    this._showRefundModal = this._showRefundModal.bind(this);
    this._closeRefundModal = this._closeRefundModal.bind(this);
    this._onRefundTickets = this._onRefundTickets.bind(this);
    this._closeRefundStatusModal = this._closeRefundStatusModal.bind(this);

    this._showBulkScanModal = this._showBulkScanModal.bind(this);

    this._showChangeSeatsModal = this._showChangeSeatsModal.bind(this);
    this._closeChangeSeatsModal = this._closeChangeSeatsModal.bind(this);
    this._onChangeSeats = this._onChangeSeats.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  _viewHistory(ticket, e) {
    e.preventDefault();
    e.stopPropagation();
    this.context.router.push(
      `/event_buckets/${
        this.props.eventBucket || ticket.event.event_bucket
      }/tickets/${ticket.id}/history`
    );
  }

  _delete(ticket, e) {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(
        `Are you sure you want to delete this ticket to "${ticket.event.name}"? There is no going back.`
      )
    ) {
      TicketActions.delete(ticket.id);
      this.state.deletedTickets[ticket.id] = true;
      this.setState(this.state);
    }
  }

  _markAsUsed(ticket, e) {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(
        `Are you sure you want to mark this ticket to "${ticket.event.name}" as scanned? It will no longer be scannable.`
      )
    ) {
      TicketActions.markAsUsed(ticket.id);
    }
  }

  _refresh(e) {
    e.preventDefault();
    e.stopPropagation();

    TicketActions.fetchAllForEvent(this.props.event);
  }

  _onCheckAll() {
    if (
      Object.keys(this.state.selectedTickets).length ===
      this.props.tickets.length
    ) {
      this.setState({ selectedTickets: {} });
    } else {
      this.setState({
        selectedTickets: this.props.tickets.reduce((d, t) => {
          d[t.id] = true;
          return d;
        }, {})
      });
    }
  }

  _onCheck(ticket) {
    if (!this.state.selectedTickets[ticket.id]) {
      this.state.selectedTickets[ticket.id] = true;
    } else {
      delete this.state.selectedTickets[ticket.id];
    }
    this.setState({ selectedTickets: this.state.selectedTickets });
  }

  _transfer(id, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ currentlyTransferring: id });
  }

  _submitTransfer(recipient) {
    TicketActions.transfer([this.state.currentlyTransferring], recipient);
    this._closeTransferModal();
  }

  _cancelTransfer(id, e) {
    e.preventDefault();
    e.stopPropagation();
    if (
      window.confirm(
        "Are you sure you wish to cancel this ticket transfer? The original transfer link will no longer work."
      )
    ) {
      TicketActions.cancelTransfer(id);
    }
  }

  _resendTransfer(id, e) {
    e.preventDefault();
    e.stopPropagation();
    TicketActions.resendTransfer(id);
  }

  _closeTransferModal() {
    this.setState({ currentlyTransferring: null });
  }

  _makeRowForTicket(ticket, showTransferHistory) {
    let isDeleted = this.state.deletedTickets[ticket.id],
      isSelected =
        this.state.selectedTickets && this.state.selectedTickets[ticket.id],
      shouldHighlight = this.state.highlightedId === ticket.id;

    let noWrapTdStyle = this.props.person ? {} : { whiteSpace: "nowrap" },
      onCheck = this._onCheck.bind(this, ticket);

    return (
      <Tr
        style={Object.assign(
          {},
          isDeleted && DELETED_ROW_STYLE,
          shouldHighlight && HIGHLIGHTED_ROW_STYLE
        )}
        key={ticket.id}
      >
        <Td column=" " value={ticket.id}>
          <div onClick={onCheck}>
            <Checkbox label=" " checked={isSelected} />
          </div>
        </Td>
        <Td column="Id">{ticket.id}</Td>
        <Td
          column="Person"
          value={`${
            ticket.person
              ? ticket.person.name || ticket.person.email
              : "Unknown Person"
          } ${ticket.pending_transfer ? ticket.pending_transfer.to_email : ""}`}
          style={noWrapTdStyle}
        >
          <span>
            {ticket.person ? (
              <span>
                {ticket.person.name || ticket.person.email}
                {!this.props.isRevshareView ? (
                  <InlineLinkIconButton
                    onTouchTap={() => {
                      this.context.router.push(
                        `/event_buckets/${
                          this.props.eventBucket || ticket.event.event_bucket
                        }/people/${ticket.person.id}`
                      );
                    }}
                    tooltip="View person"
                  />
                ) : null}
              </span>
            ) : (
              "Unknown Person"
            )}
            {ticket.pending_transfer ? (
              <span style={{ whiteSpace: "break-spaces" }}>
                → {ticket.pending_transfer.to_email}
              </span>
            ) : null}
          </span>
        </Td>
        <Td
          column="Pass Used"
          value={ticket.pass ? ticket.pass.name : "n/a"}
          style={noWrapTdStyle}
        >
          {ticket.pass ? (
            <div>
              {ticket.pass.name}
              {!this.props.isRevshareView ? (
                <InlineLinkIconButton
                  onTouchTap={() => {
                    this.context.router.push(
                      `/event_buckets/${
                        this.props.eventBucket || ticket.event.event_bucket
                      }/people/${
                        this.props.person || ticket.person.id
                      }?highlight_pass=${ticket.pass.id}`
                    );
                  }}
                  tooltip="View pass"
                />
              ) : null}
            </div>
          ) : (
            "n/a"
          )}
        </Td>
        <Td column="Event" value={ticket.event.name}>
          <div>
            {ticket.event.name}
            <InlineLinkIconButton
              onTouchTap={() => {
                this.context.router.push(
                  `/event_buckets/${
                    this.props.eventBucket || ticket.event.event_bucket
                  }/events/${ticket.event.id}`
                );
              }}
              tooltip="View event"
            />
          </div>
        </Td>
        <Td column="Ticket Bucket" style={noWrapTdStyle}>
          {`${ticket.ticket_bucket.name}${
            ticket.variant ? ` – ${ticket.variant.name}` : ""
          }`}
        </Td>
        <Td column="Seat" style={noWrapTdStyle}>
          {ticket.seat}
        </Td>
        <Td
          column="Unlock Status"
          value={
            ticket.expires_at && moment(ticket.expires_at) < Date.now()
              ? `expires-${ticket.expires_at}`
              : `unlock-${ticket.unlocked_until}`
          }
        >
          {ticket.expires_at ? (
            <span>
              {moment(ticket.expires_at) < Date.now()
                ? `Viewing complete; expired`
                : `Started watching; expires`}{" "}
              <span style={{ whiteSpace: "nowrap" }}>
                {moment(ticket.expires_at).format("lll")}
              </span>
            </span>
          ) : (
            <span>
              {ticket.event.is_virtual
                ? moment(ticket.unlocked_until) < Date.now()
                  ? `Never watched; unlock window closed`
                  : `Not yet watched; unlocked until`
                : `Physical ticket`}{" "}
              <span style={{ whiteSpace: "nowrap" }}>
                {ticket.event.is_virtual
                  ? moment(ticket.unlocked_until).format("lll")
                  : ""}
              </span>
            </span>
          )}
        </Td>
        <Td
          column="Event Date"
          value={ticket.event.start_time}
          style={noWrapTdStyle}
        >
          {moment(ticket.event.start_time).format("lll")}
        </Td>
        <Td column="Order Date" value={ticket.created_at} style={noWrapTdStyle}>
          <div>
            {moment(ticket.created_at).format("lll")}
            {!this.props.isRevshareView ? (
              <InlineLinkIconButton
                onTouchTap={() => {
                  this.context.router.push(
                    `/event_buckets/${
                      this.props.eventBucket || ticket.event.event_bucket
                    }/orders/${this.props.order || ticket.order}`
                  );
                }}
                tooltip="View order"
              />
            ) : null}
          </div>
        </Td>
        <Td column="Scan Date" value={ticket.used_at} style={noWrapTdStyle}>
          {ticket.used_at
            ? moment(ticket.used_at).format("lll")
            : "Not scanned"}
        </Td>
        {UserStore.hasPermission("manage_orders") ? (
          <Td column="Actions" style={noWrapTdStyle}>
            {
              <div>
                <div
                  ref={r => {
                    if (shouldHighlight) {
                      this.highlightedRow = r;
                    }
                  }}
                />
                {ticket.pending_transfer &&
                UserStore.hasPermission("manage_orders") ? (
                  <div>
                    <i
                      title={`Pending transfer to ${ticket.pending_transfer.to_email}`}
                    >
                      Pending transfer&nbsp;
                      <small>
                        [
                        <a
                          style={isDeleted && { color: "#ccc" }}
                          href="#"
                          onClick={this._cancelTransfer.bind(
                            this,
                            ticket.pending_transfer.id
                          )}
                        >
                          Cancel
                        </a>{" "}
                        |{" "}
                        <a
                          style={isDeleted && { color: "#ccc" }}
                          href="#"
                          onClick={this._resendTransfer.bind(
                            this,
                            ticket.pending_transfer.id
                          )}
                        >
                          Resend
                        </a>
                        ]
                      </small>
                    </i>
                  </div>
                ) : UserStore.hasPermission("manage_orders") ? (
                  <span>
                    <a
                      style={isDeleted && { color: "#ccc" }}
                      href="#"
                      onClick={this._transfer.bind(this, ticket.id)}
                    >
                      Transfer
                    </a>
                    &nbsp;
                  </span>
                ) : null}
                {UserStore.hasPermission("manage_orders") ? (
                  <span>
                    <a
                      style={isDeleted && { color: "#ccc" }}
                      href="#"
                      onClick={() => this._showExchangeModal(ticket.id)}
                    >
                      Exchange
                    </a>
                  </span>
                ) : null}
                {!ticket.used_at &&
                  UserStore.hasPermission("manage_orders") && (
                    <span>
                      &nbsp;
                      <a
                        href="#"
                        style={isDeleted && { color: "#ccc" }}
                        onClick={this._markAsUsed.bind(this, ticket)}
                      >
                        Mark as scanned
                      </a>
                    </span>
                  )}
                {!isDeleted && UserStore.hasPermission("manage_orders") && (
                  <span>
                    &nbsp;
                    <a href="#" onClick={this._delete.bind(this, ticket)}>
                      Delete
                    </a>
                  </span>
                )}
                {UserStore.isGod() || showTransferHistory ? (
                  <span>
                    &nbsp;
                    <a href="#" onClick={this._viewHistory.bind(this, ticket)}>
                      View History
                    </a>
                  </span>
                ) : null}
              </div>
            }
          </Td>
        ) : null}
      </Tr>
    );
  }

  _onExtensionSubmit(form) {
    TicketActions.changeTicketsUnlock({
      ...form,
      ticketIds: Object.keys(this.state.selectedTickets)
    });
  }

  _onExtensionFormClose() {
    this.setState({ showExtensionsModal: false });
  }

  _showExchangeModal(ticketId) {
    this.setState({ bulkActionsPopoverOpen: false });
    const ticketsToExchange = ticketId
      ? [ticketId]
      : Object.keys(this.state.selectedTickets);
    this.setState({
      ticketsToExchange: ticketsToExchange.map(id =>
        this.props.tickets.find(t => t.id === id)
      )
    });
  }

  _closeExchangeModal() {
    this.setState({ ticketsToExchange: null });
  }

  _onExchangeTickets(ticketBucketId, updatedSeats) {
    TicketActions.exchangeTickets({
      ticketBucketId,
      ticketIds: this.state.ticketsToExchange.map(t => t.id),
      updatedSeats
    });
    this._closeExchangeModal();
  }

  _showDeleteModal(ticketId) {
    this.setState({ bulkActionsPopoverOpen: false });
    const ticketsToDelete = ticketId
      ? [ticketId]
      : Object.keys(this.state.selectedTickets);

    this.setState({
      ticketsToDelete: ticketsToDelete.map(id =>
        this.props.tickets.find(t => t.id === id)
      )
    });
  }

  _closeDeleteModal() {
    this.setState({ ticketsToDelete: null });
  }

  _onDeleteTickets() {
    const ticketIds = this.state.ticketsToDelete.map(t => t.id);
    TicketActions.deleteTickets({
      ticket_ids: ticketIds
    });

    for (const ticketId of ticketIds) {
      this.state.deletedTickets[ticketId] = true;
    }

    this._closeDeleteModal();
    this.setState({
      selectedTickets: {}
    });
  }

  async _showRefundModal() {
    this.setState({ bulkActionsPopoverOpen: false });
    const ticketIdsToRefund = Object.keys(this.state.selectedTickets);
    const ticketsToRefund = ticketIdsToRefund.map(id =>
      this.props.tickets.find(t => t.id === id)
    );

    // deduplicate the orders list
    const ordersToRefund = Array.from(
      new Set(ticketsToRefund.map(t => t.order || this.props.order))
    );

    const apiKey = UserStore.getState().apiKey;

    // get the refund options for all of the orders
    const refundOptions = await Promise.all(
      ordersToRefund.map(async orderId => {
        try {
          return await OrderUtil.generateRefundOptions(apiKey, orderId);
        } catch (error) {
          return { error };
        }
      })
    );

    // merge the options into one array
    const mergedRefundOptions = refundOptions.reduce((result, obj) => {
      return result.concat(obj.options);
    }, []);

    // filter out only the relevant refund options to the tickets selected
    const filteredRefundOpts = mergedRefundOptions.filter(option => {
      if (!option || option.item.ref_type !== "Ticket") return false;

      return ticketIdsToRefund.includes(option.item.ref_id);
    });

    this.setState({
      ticketsToRefund: ticketsToRefund,
      refundOptions: filteredRefundOpts
    });
  }

  async _onRefundTickets(options) {
    const optionsByOrder = {};
    for (const key in options.refunds) {
      const keySplit = key.split("-");
      const item = options.refunds[key];
      const id = keySplit[1];

      const ticket = this.props.tickets.find(t => t.id === id);
      const order = ticket.order || this.props.order;

      if (!optionsByOrder[order]) {
        optionsByOrder[order] = {};
      }

      optionsByOrder[order][key] = item;
    }

    this.setState({
      ticketsToRefund: null,
      refundOptions: null,
      bulkRefunding: true,
      bulkRefundingOrderCount: Object.keys(optionsByOrder).length,
      bulkRefundingProgress: 0
    });

    let progress = 0;
    for (const orderId in optionsByOrder) {
      try {
        progress += 1;

        this.setState({
          bulkRefundingProgress: progress
        });

        const data = await OrderUtil.refund(
          UserStore.getState().apiKey,
          orderId,
          {
            refunds: optionsByOrder[orderId]
          }
        );

        for (const option in optionsByOrder[orderId]) {
          if (!option.startsWith("TICKET")) continue;

          const ticketId = option.replace("TICKET-", "");
          this.state.deletedTickets[ticketId] = true;
        }

        OrderActions.updateOrder(data);
        LedgerActions.fetchLedgerTransactionsForOrder.defer(orderId); // force ledger update
      } catch (err) {
        console.error(`refund on order ${orderId} failed`);
      }
    }

    this.setState({
      bulkRefundingComplete: true
    });
  }

  _closeRefundModal() {
    this.setState({
      ticketsToRefund: null,
      refundOptions: null
    });
  }

  _closeRefundStatusModal() {
    this.setState({
      ticketsToRefund: null,
      refundOptions: null,
      bulkRefunding: false,
      bulkRefundingOrderCount: 0,
      bulkRefundingProgress: 0
    });
  }

  async _showBulkScanModal() {
    this.setState({ bulkActionsPopoverOpen: false });
    const ticketsToScan = this.ticketsEligibleForScanning(
      this.state.selectedTickets
    );

    const plural = ticketsToScan.length !== 1;
    const ticketNames = Array.from(
      new Set(ticketsToScan.map(t => `"${t.event.name}"`))
    ).join(", ");

    if (
      window.confirm(
        `Are you sure you want to mark the selected ${
          ticketsToScan.length
        } ticket${plural ? "s" : ""} to ${ticketNames} as scanned? ${
          plural ? "They" : "It"
        } will no longer be scannable.`
      )
    ) {
      ticketsToScan.forEach(t => {
        TicketActions.markAsUsed(t.id);
      });
    }
  }

  _showChangeSeatsModal(ticketId) {
    this.setState({ bulkActionsPopoverOpen: false });
    const ticketIds = ticketId
      ? [ticketId]
      : Object.keys(this.state.selectedTickets);

    this.setState({
      ticketsToChangeSeats: ticketIds.map(id =>
        this.props.tickets.find(t => t.id === id)
      )
    });
  }

  _closeChangeSeatsModal() {
    this.setState({ ticketsToChangeSeats: null });
  }

  _onChangeSeats(seatChangePayload) {
    TicketActions.changeSeats(seatChangePayload);
    this._closeChangeSeatsModal();
  }

  ticketsIndexToArray(ticketsIndex) {
    return Object.keys(ticketsIndex).map(id =>
      this.props.tickets.find(t => t.id === id)
    );
  }

  ticketsEligibleForScanning(ticketsIndex) {
    const tickets = this.ticketsIndexToArray(ticketsIndex);

    const ticketsEligible = tickets.filter(t => !t.used_at);

    return ticketsEligible;
  }

  componentWillReceiveProps(props) {
    if (
      props.location &&
      props.location.query.ticket &&
      this.table &&
      this.table.data.length
    ) {
      let rowLocation = this.table.data.findIndex(
          p => p.data["Id"].value === props.location.query.ticket
        ),
        neededPage = Math.floor(rowLocation / PAGE_SIZE);

      this.setState({
        highlightedId: props.location.query.ticket,
        currentPage: neededPage
      });
    }
    if (!props.isFetching) {
      this.setState({ showExtensionsModal: false });
    }
  }

  componentDidUpdate() {
    if (
      this.highlightedRow &&
      this.state.highlightedRow !== this.highlightedRow
    ) {
      this.setState(
        {
          highlightedRow: this.highlightedRow
        },
        () => {
          setTimeout(() => {
            if (
              this.highlightedRow &&
              this.highlightedRow.scrollIntoViewIfNeeded
            ) {
              // chrome only
              this.highlightedRow.scrollIntoViewIfNeeded();
            } else if (this.highlightedRow) {
              this.highlightedRow.scrollIntoView();
            }
          }, 0);
        }
      );
    }
  }

  render() {
    const isSundance = this.props.isSundance;
    const showTransferHistory = this.props.showTransferHistory;
    //let rows = this.props.tickets.map(this._makeRowForTicket),
    let rows = this.props.tickets.map(function(e) { return this._makeRowForTicket(e, isSundance || showTransferHistory); }.bind(this)),
      ticketCount = this.props.tickets.length,
      scannedCount = this.props.tickets.filter(t => t.used_at).length,
      passTicketCount = this.props.tickets.filter(t => t.pass).length,
      passTicketScannedCount = this.props.tickets.filter(
        t => t.pass && t.used_at
      ).length,
      anyPassesUsed = this.props.tickets.some(t => !!t.pass),
      anySeatsUsed = this.props.tickets.some(t => !!t.seat),
      anyUnlocks = this.props.tickets.some(t => !!t.unlocked_until),
      selectedTicketCount = Object.keys(this.state.selectedTickets).length,
      allTicketsSelected = [0, rows.length].indexOf(selectedTicketCount) !== -1,
      pendingTransferCount = this.props.tickets.filter(
        t => t.pending_transfer
      ).length;
    const {
      ticketsToExchange,
      ticketsToChangeSeats,
      ticketsToDelete,
      ticketsToRefund,
      refundOptions
    } = this.state;
    const { tickets, eventBucket } = this.props;

    const selectedTicketsEligibleForScanning = this.ticketsEligibleForScanning(
      this.state.selectedTickets
    );

    const ticketPluralized = "ticket" + (selectedTicketCount === 1 ? "" : "s");

    return (
      <div>
        {this.props.showStats ? (
          <StatsBlockContainer>
            <TicketsAtAGlance event={this.props.eventDetails} />
            <StatsBlock
              value={scannedCount}
              subvalue={ticketCount || "0"}
              label="Tickets scanned"
            />
            {anyPassesUsed ? (
              <StatsBlock
                value={passTicketScannedCount}
                subvalue={passTicketCount || "0"}
                label="Passholder tickets scanned"
              />
            ) : null}
          </StatsBlockContainer>
        ) : null}
        <div
          style={
            this.props.order || this.props.person
              ? { float: "right", marginBottom: -36, marginRight: "-1em" }
              : { position: "absolute", right: 40, marginTop: 18 }
          }
        >
          {rows.length && selectedTicketCount > 0 ? (
            <div style={{ display: "inline-block" }}>
              <RaisedButton
                label={`Bulk Actions (${selectedTicketCount})`}
                style={{ marginRight: "1em" }}
                onTouchTap={e => {
                  e.preventDefault();
                  this.setState({
                    bulkActionsPopoverOpen: true,
                    bulkActionsPopoverAnchorEl: e.currentTarget
                  });
                }}
              />
              <Popover
                open={this.state.bulkActionsPopoverOpen}
                anchorEl={this.state.bulkActionsPopoverAnchorEl}
                onRequestClose={() =>
                  this.setState({ bulkActionsPopoverOpen: false })
                }
              >
                <Menu desktop={true}>
                  {UserStore.isGod() ? (
                    <MenuItem
                      disabled={selectedTicketCount === 0}
                      primaryText={`Extend ${selectedTicketCount} ticket${
                        selectedTicketCount === 1 ? "" : "s"
                      }`}
                      onTouchTap={() =>
                        this.setState({
                          showExtensionsModal: true,
                          bulkActionsPopoverOpen: false
                        })
                      }
                    />
                  ) : null}

                  <MenuItem
                    disabled={selectedTicketCount === 0}
                    primaryText={`Refund ${selectedTicketCount} ${ticketPluralized}`}
                    onTouchTap={() => this._showRefundModal()}
                    style={{ marginRight: "1em" }}
                  />
                  <MenuItem
                    disabled={selectedTicketsEligibleForScanning.length === 0}
                    primaryText={`Mark ${
                      selectedTicketsEligibleForScanning.length
                    } ticket${
                      selectedTicketsEligibleForScanning.length !== 1 ? "s" : ""
                    } as scanned`}
                    onTouchTap={() => this._showBulkScanModal()}
                    style={{ marginRight: "1em" }}
                  />
                  <MenuItem
                    disabled={selectedTicketCount === 0}
                    primaryText={`Delete ${selectedTicketCount} ticket${
                      selectedTicketCount === 1 ? "" : "s"
                    }`}
                    onTouchTap={() => this._showDeleteModal()}
                    style={{ marginRight: "1em" }}
                  />

                  {this.props.tickets.some(t => t.seat) ? (
                    <MenuItem
                      disabled={selectedTicketCount === 0}
                      primaryText={`Change ${selectedTicketCount} seat${
                        selectedTicketCount === 1 ? "" : "s"
                      }`}
                      onTouchTap={() => this._showChangeSeatsModal()}
                      style={{ marginRight: "1em" }}
                    />
                  ) : null}
                  {this.props.order || this.props.person ? (
                    <MenuItem
                      disabled={selectedTicketCount === 0}
                      primaryText={`Exchange ${selectedTicketCount} ticket${
                        selectedTicketCount === 1 ? "" : "s"
                      }`}
                      onTouchTap={() => this._showExchangeModal()}
                      style={{ marginRight: "1em" }}
                    />
                  ) : null}
                  {pendingTransferCount > 0 && !this.props.isRevshareView ? (
                    <MenuItem
                      primaryText={`Resend transfer${
                        pendingTransferCount === 1 ? "" : "s"
                      }`}
                      onTouchTap={() => {
                        if (
                          window.confirm(
                            `This will immediately resend the ticket transfer email to all ${pendingTransferCount} customer${
                              pendingTransferCount === 1 ? "" : "s"
                            } who have not yet accepted their transfer.\n\nPlease use this option with care, as repeated use could lead to your emails being marked as SPAM.\n\nAre you sure you would like to continue?`
                          )
                        ) {
                          TicketActions.resendTransfer(
                            this.props.tickets
                              .filter(t => t.pending_transfer)
                              .map(t => t.pending_transfer.id)
                          );
                        }
                      }}
                      style={{ marginRight: "1em" }}
                    />
                  ) : null}
                </Menu>
              </Popover>
            </div>
          ) : null}

          {(this.props.order || this.props.person) && rows.length ? (
            <DownloadTicketsPdfButton
              {...this.props}
              tickets={Object.keys(this.state.selectedTickets)}
              label={`Download ${
                allTicketsSelected
                  ? `all ${selectedTicketCount || rows.length}`
                  : selectedTicketCount
              } ticket${
                selectedTicketCount === 1 || rows.length === 1 ? "" : "s"
              }`}
              style={{ marginRight: "1em" }}
            />
          ) : null}
        </div>
        {this.props.event ? (
          <TableRefreshIndicator
            isRefreshing={this.props.isFetching}
            onRefresh={this._refresh}
            style={{ marginTop: 15 }}
          />
        ) : null}
        <Table
          ref={r => {
            this.table = r;
          }}
          className="bordered striped responsive-table"
          sortable={[
            { column: "Order Date", sortFunction: Sort.Date },
            { column: "Scan Date", sortFunction: Sort.Date },
            { column: "Unlock Status", sortFunction: Sort.CaseInsensitive },
            { column: "Event Date", sortFunction: Sort.Date },
            { column: "Event", sortFunction: Sort.CaseInsensitive },
            "Ticket Bucket",
            { column: "Pass Used", sortFunction: Sort.CaseInsensitive },
            { column: "Person", sortFunction: Sort.CaseInsensitive }
          ]}
          defaultSort={{ column: "Event Date", direction: "asc" }}
          columns={(rows.length
            ? [
                {
                  key: " ",
                  label: (
                    <Checkbox
                      iconStyle={{ fill: "#fff" }}
                      checked={selectedTicketCount === rows.length}
                      onCheck={this._onCheckAll}
                    />
                  )
                }
              ]
            : []
          )
            .concat(this.props.showEvent ? ["Event"] : [])
            .concat(
              this.props.showPerson || pendingTransferCount ? ["Person"] : []
            )
            .concat(anyPassesUsed ? ["Pass Used"] : [])
            .concat(["Ticket Bucket"])
            .concat(anySeatsUsed ? ["Seat"] : [])
            .concat(this.props.showEvent ? ["Event Date"] : [])
            .concat(["Order Date", "Scan Date"])
            .concat(anyUnlocks ? ["Unlock Status"] : [])
            .concat(
              UserStore.hasPermission("manage_orders") &&
                !this.props.isRevshareView
                ? ["Actions"]
                : []
            )}
          filterable={["Person", "Event", "Pass Used", "Name", "Ticket Bucket"]}
          filterPlaceholder="Type to filter..."
          itemsPerPage={ticketCount > PAGE_SIZE ? PAGE_SIZE : undefined}
          currentPage={this.state.currentPage}
          onPageChange={p => this.setState({ currentPage: p })}
        >
          {rows}
        </Table>
        {rows.length === 0 && !this.props.hideNoTicketsMessage ? (
          <div className="empty-table-message">No tickets yet.</div>
        ) : null}
        {UserStore.isGod() && selectedTicketCount ? (
          <div
            style={{
              marginTop: 5,
              color: "rgba(0,0,0,0.6)",
              fontSize: "0.8em"
            }}
          >
            [God Only] Selected tickets:{" "}
            {Object.keys(this.state.selectedTickets).join(", ")}
          </div>
        ) : null}
        <GodTicketsExtension
          open={this.state.showExtensionsModal}
          submitting={this.props.isFetching}
          ticketsLength={selectedTicketCount}
          onClose={this._onExtensionFormClose}
          submit={this._onExtensionSubmit}
        />
        {!!ticketsToExchange ? (
          <ExchangeOverlay
            submitting={this.props.isFetching}
            tickets={ticketsToExchange}
            eventBucketId={
              eventBucket || (tickets[0] ? tickets[0].event.event_bucket : "")
            }
            onClose={this._closeExchangeModal}
            onExchange={this._onExchangeTickets}
          />
        ) : null}
        {this.state.bulkRefunding ? (
          <LoadingOverlay
            onRequestClose={this._closeRefundStatusModal}
            message={
              this.state.bulkRefundingComplete
                ? `Refunds complete!`
                : `Refunding ${this.state.bulkRefundingProgress}/${this.state.bulkRefundingOrderCount} orders`
            }
            complete={this.state.bulkRefundingComplete}
          />
        ) : null}
        {!!ticketsToRefund ? (
          <RefundOverlay
            options={refundOptions}
            onRefund={this._onRefundTickets}
            refundErrorMessage={this.state.refundErrorMessage}
            onRequestClose={this._closeRefundModal}
          />
        ) : null}
        {!!ticketsToDelete ? (
          <BulkDeleteOverlay
            submitting={this.props.isFetching}
            items={ticketsToDelete}
            itemName={`ticket${ticketsToDelete.length !== 1 ? "s" : ""}`}
            eventBucketId={
              eventBucket || (tickets[0] ? tickets[0].event.event_bucket : "")
            }
            onClose={this._closeDeleteModal}
            onDelete={this._onDeleteTickets}
          />
        ) : null}
        {!!ticketsToChangeSeats ? (
          <ChangeSeatsOverlay
            submitting={this.props.isFetching}
            tickets={ticketsToChangeSeats}
            onClose={this._closeChangeSeatsModal}
            onChangeSeats={this._onChangeSeats}
          />
        ) : null}
        {this.state.currentlyTransferring ? (
          <TransferOverlay
            onRequestClose={this._closeTransferModal}
            onTransfer={this._submitTransfer}
            type="ticket"
          />
        ) : null}
      </div>
    );
  }
}

export default TicketTable;



// WEBPACK FOOTER //
// ./src/app/components/partials/ticket-table.jsx